import React, { useState, useContext, useEffect } from 'react';
import styled from 'styled-components';
import { ThemeContext } from '../../contexts/ThemeContext';
import { authFetch } from '../../services/authFetch';

// Notification Toast
const NotificationToast = styled.div`
  position: fixed;
  bottom: 20px;
  right: 20px;
  min-width: 300px;
  background-color: ${props => props.$type === 'success' ? '#2ecc71' : '#e74c3c'};
  color: white;
  padding: 16px 20px;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  display: flex;
  justify-content: space-between;
  align-items: center;
  z-index: 9999;
  animation: slideIn 0.3s ease-out forwards;
  @keyframes slideIn {
    from { transform: translateX(100%); opacity: 0; }
    to { transform: translateX(0); opacity: 1; }
  }
`;
const NotificationMessage = styled.div`
  flex: 1;
  font-weight: 500;
`;
const CloseNotificationButton = styled.button`
  background: transparent;
  border: none;
  color: white;
  font-size: 20px;
  cursor: pointer;
  margin-left: 10px;
  opacity: 0.8;
  transition: opacity 0.2s;
  &:hover { opacity: 1; }
`;

const InterupsiForm = ({ onClose, initialData = null, readOnly = false, status = null, statusText = null, isStudentForm = false }) => {
  const { darkMode } = useContext(ThemeContext);
  const [formData, setFormData] = useState({
    classType: isStudentForm ? 'Teori' : 'Praktek', // Default to 'Teori' for students, 'Praktek' for teachers
    departureTime: '',
    returnTime: '',
    purpose: '',
    destination: '',
    responsible: '',
    relationship: '',
    phoneNumber: '', // optional, can be empty
    student_id: '' // new field for student selection
  });
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });

  // Student selection states
  const [students, setStudents] = useState([]);
  const [studentSearch, setStudentSearch] = useState('');
  const [showStudentSuggestions, setShowStudentSuggestions] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);

  // Fetch students for selection (only for teacher/admin forms)
  useEffect(() => {
    if (!isStudentForm) {
      const fetchStudents = async () => {
        try {
          const response = await authFetch('/api/kts-students');
          if (response.ok) {
            const result = await response.json();
            if (result.success) {
              setStudents(result.data || []);
            }
          }
        } catch (error) {
          console.error('Error fetching students:', error);
        }
      };

      fetchStudents();
    }
  }, [isStudentForm]);

  // Load initial data if provided
  useEffect(() => {
    if (initialData) {
      setFormData(initialData);
      // If there's a student_id in initial data, find and set the selected student
      if (initialData.student_id && students.length > 0) {
        const student = students.find(s => s.id === initialData.student_id);
        if (student) {
          setSelectedStudent(student);
          setStudentSearch(student.name);
        }
      }
    }
  }, [initialData, students]);

  const handleChange = (e) => {
    if (readOnly) return;

    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleClassTypeChange = (type) => {
    if (readOnly) return;

    setFormData({
      ...formData,
      classType: type
    });
  };

  // Student selection handlers
  const handleStudentSearch = (e) => {
    const value = e.target.value;
    setStudentSearch(value);
    setShowStudentSuggestions(value.length > 0);

    if (value === '') {
      setSelectedStudent(null);
      setFormData({ ...formData, student_id: '' });
    }
  };

  const handleStudentSelect = (student) => {
    setSelectedStudent(student);
    setStudentSearch(student.name);
    setShowStudentSuggestions(false);
    setFormData({ ...formData, student_id: student.id });
  };

  const filteredStudents = students.filter(student =>
    student.name.toLowerCase().includes(studentSearch.toLowerCase())
  ).slice(0, 10); // Limit to 10 suggestions

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (readOnly) {
      onClose();
      return;
    }
    setNotification({ show: false, message: '', type: 'success' });
    setLoading(true);

    // Handle cross-midnight scenarios by adjusting times
    let leaveHourToSend = formData.departureTime;
    let returnHourToSend = formData.returnTime;

    if (formData.departureTime && formData.returnTime) {
      const [startHour, startMinute] = formData.departureTime.split(':').map(Number);
      const [finishHour, finishMinute] = formData.returnTime.split(':').map(Number);
      const startTotalMinutes = startHour * 60 + startMinute;
      const finishTotalMinutes = finishHour * 60 + finishMinute;

      if (finishTotalMinutes <= startTotalMinutes) {
        // For cross-midnight scenarios, we'll send the times as-is
        // The backend should handle this appropriately
        console.log('Cross-midnight time detected:', {
          departure: formData.departureTime,
          return: formData.returnTime,
          note: 'Return time appears to be next day - sending times as-is'
        });
      }
    }

    // Map frontend fields to backend
    const payload = {
      current_activity: formData.classType, // 'Teori' or 'Praktek'
      leave_hour: leaveHourToSend,
      return_hour: returnHourToSend,
      purpose: formData.purpose,
      destination: formData.destination,
      pic: formData.responsible,
      relationship: formData.relationship,
      pic_contact_number: formData.phoneNumber || undefined
    };

    // Add student_id only for teacher/admin forms
    if (!isStudentForm) {
      payload.student_id = formData.student_id;
    }

    try {
      const apiEndpoint = isStudentForm ? '/api/interuption_permits' : '/api/teacher/interuption-permits';
      const response = await authFetch(apiEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });
      const responseText = await response.clone().text();
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseErr) {
        setNotification({ show: true, message: 'Gagal mengurai respons server.', type: 'error' });
        setLoading(false);
        return;
      }
      if (!response.ok) {
        // Enhanced error reporting for validation failures
        if (response.status === 422 && data.errors) {
          const errorMessages = Object.values(data.errors).flat().join(' ');
          setNotification({ show: true, message: `Gagal validasi: ${errorMessages}`, type: 'error' });
        } else {
          console.error('Server response:', response);
          console.error('Server response data:', data);
          setNotification({ show: true, message: data.message || 'Gagal mengirim permohonan interupsi.', type: 'error' });
        }
      } else {
        setNotification({ show: true, message: data.message || 'Permohonan Interupsi berhasil diajukan!', type: 'success' });
        setFormData({
          classType: isStudentForm ? 'Teori' : 'Praktek',
          departureTime: '',
          returnTime: '',
          purpose: '',
          destination: '',
          responsible: '',
          relationship: '',
          phoneNumber: '',
          student_id: ''
        });
        setStudentSearch('');
        setSelectedStudent(null);
        // Close the popup after a short delay if success
        if (onClose) {
          setTimeout(() => {
            onClose();
          }, 2000);
        }
      }
    } catch (err) {
      setNotification({ show: true, message: 'Terjadi kesalahan jaringan.', type: 'error' });
    }
    setLoading(false);
  };

  return (
    <FormWrapper theme={darkMode ? 'dark' : 'light'}>
      <FormHeader>
        <HeaderContent>
          <h2>Formulir Permohonan Interupsi</h2>
          {status && (
            <StatusBadge status={status}>
              {statusText}
            </StatusBadge>
          )}
        </HeaderContent>
        <CloseButton onClick={onClose}>×</CloseButton>
      </FormHeader>

      <FormContent>
        {notification.show && (
          <NotificationToast $type={notification.type}>
            <NotificationMessage>{notification.message}</NotificationMessage>
            <CloseNotificationButton onClick={() => setNotification({ ...notification, show: false })}>&times;</CloseNotificationButton>
          </NotificationToast>
        )}
        <form onSubmit={handleSubmit}>
          {/* Student Selection Field - Only show for teacher/admin forms */}
          {!isStudentForm && (
            <FormGroup theme={darkMode ? 'dark' : 'light'}>
              <label htmlFor="studentSearch">Nama Siswa</label>
              <div style={{ position: 'relative' }}>
                <input
                  type="text"
                  id="studentSearch"
                  value={studentSearch}
                  onChange={handleStudentSearch}
                  placeholder="Cari nama siswa..."
                  required
                  readOnly={readOnly}
                  disabled={readOnly}
                  onFocus={() => setShowStudentSuggestions(studentSearch.length > 0)}
                  onBlur={() => setTimeout(() => setShowStudentSuggestions(false), 200)}
                />
                {showStudentSuggestions && filteredStudents.length > 0 && (
                  <StudentSuggestions>
                    {filteredStudents.map((student) => (
                      <StudentSuggestion
                        key={student.id}
                        onClick={() => handleStudentSelect(student)}
                      >
                        {student.name} - {student.nis}
                      </StudentSuggestion>
                    ))}
                  </StudentSuggestions>
                )}
              </div>
            </FormGroup>
          )}

          <AlertWarning>
            <AlertIcon>⚠️</AlertIcon>
            <AlertText>Untuk interupsi kelas praktek, hubungi guru praktek</AlertText>
          </AlertWarning>

          <TypeToggleContainer>
            <TypeToggleLabel>Saat ini sedang apa?</TypeToggleLabel>
            <TypeToggleButtonGroup>
              {isStudentForm ? (
                // For students, only show "Teori" option
                <TypeToggleButton
                  type="button"
                  active="true"
                  disabled={true}
                >
                  Teori
                </TypeToggleButton>
              ) : (
                // For teachers/admins, show "Praktek" option
                <TypeToggleButton
                  type="button"
                  active="true"
                  onClick={() => handleClassTypeChange('Praktek')}
                  disabled={readOnly}
                >
                  Praktek
                </TypeToggleButton>
              )}
            </TypeToggleButtonGroup>
          </TypeToggleContainer>

          <FormRow>
            <FormGroup theme={darkMode ? 'dark' : 'light'}>
              <label htmlFor="departureTime">Pergi jam berapa</label>
              <input
                type="time"
                id="departureTime"
                name="departureTime"
                value={formData.departureTime}
                onChange={handleChange}
                required
                readOnly={readOnly}
                disabled={readOnly}
              />
            </FormGroup>

            <FormGroup theme={darkMode ? 'dark' : 'light'}>
              <label htmlFor="returnTime">Kembali</label>
              <input
                type="time"
                id="returnTime"
                name="returnTime"
                value={formData.returnTime}
                onChange={handleChange}
                readOnly={readOnly}
                disabled={readOnly}
              />
            </FormGroup>
          </FormRow>

          <FormGroup theme={darkMode ? 'dark' : 'light'}>
            <label htmlFor="purpose">Ada keperluan apa</label>
            <textarea
              id="purpose"
              name="purpose"
              rows="3"
              value={formData.purpose}
              onChange={handleChange}
              placeholder="Tuliskan alasan/keperluan interupsi"
              required
              readOnly={readOnly}
              disabled={readOnly}
            ></textarea>
          </FormGroup>

          <FormGroup theme={darkMode ? 'dark' : 'light'}>
            <label htmlFor="destination">Nama tempat dan alamat tujuan</label>
            <textarea
              id="destination"
              name="destination"
              rows="2"
              value={formData.destination}
              onChange={handleChange}
              placeholder="Contoh: Klinik Sehat, Jl. Pahlawan No. 10"
              required
              readOnly={readOnly}
              disabled={readOnly}
            ></textarea>
          </FormGroup>

          <FormRow>
            <FormGroup theme={darkMode ? 'dark' : 'light'}>
              <label htmlFor="responsible">Nama penanggungjawab <span style={{ color: '#888', fontWeight: 400 }}>(opsional)</span></label>
              <input
                type="text"
                id="responsible"
                name="responsible"
                value={formData.responsible}
                onChange={handleChange}
                placeholder="Contoh: Budi Santoso"
                readOnly={readOnly}
                disabled={readOnly}
              />
            </FormGroup>

            <FormGroup theme={darkMode ? 'dark' : 'light'}>
              <label htmlFor="relationship">Hubungan <span style={{ color: '#888', fontWeight: 400 }}>(opsional)</span></label>
              <input
                type="text"
                id="relationship"
                name="relationship"
                value={formData.relationship}
                onChange={handleChange}
                placeholder="Contoh: Orang tua, Wali, Saudara"
                readOnly={readOnly}
                disabled={readOnly}
              />
            </FormGroup>
          </FormRow>

          <FormGroup theme={darkMode ? 'dark' : 'light'}>
            <label htmlFor="phoneNumber">Nomor telepon penanggungjawab <span style={{ color: '#888', fontWeight: 400 }}>(opsional)</span></label>
            <input
              type="tel"
              pattern="[0-9]*"
              inputMode="numeric"
              id="phoneNumber"
              name="phoneNumber"
              value={formData.phoneNumber}
              onChange={e => {
                // Only allow digits
                if (/^\d*$/.test(e.target.value)) handleChange(e);
              }}
              placeholder="Contoh: 081234567890"
              readOnly={readOnly}
              disabled={readOnly}
            />
          </FormGroup>

          <NoteBox>
            <NoteIcon>ℹ️</NoteIcon>
            <NoteText>Catatan: Siswa wajib melapor ke pamong setelah interupsi selesai.</NoteText>
          </NoteBox>

          <FormActions>
            <CancelButton type="button" onClick={onClose}>
              {readOnly ? 'Tutup' : 'Batal'}
            </CancelButton>
            {!readOnly && (
              <SubmitButton type="submit" disabled={loading}>
                {loading ? 'Mengirim...' : 'Kirim Permohonan'}
              </SubmitButton>
            )}
          </FormActions>
        </form>
      </FormContent>
    </FormWrapper>
  );
};

const FormWrapper = styled.div`
  background-color: var(--background);
  border-radius: 16px;
  overflow: hidden;
  transition: background-color 0.3s;
`;

const FormHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.5rem;
  border-bottom: 1px solid var(--border);
  
  @media (max-width: 768px) {
    padding: 1rem 0.5rem;
  }
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  
  h2 {
    font-size: 1.25rem;
    font-weight: 600;
    color: var(--text-dark);
    margin: 0;
  }
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 0.5rem 1rem;
  border-radius: 1rem;
  font-size: 0.95rem;
  font-weight: 600;
  
  ${props => {
    switch (props.status) {
      case 'approved':
        return `
          background-color: #e6f8f5;
          color: #00a389;
        `;
      case 'pending':
        return `
          background-color: #fff3e0;
          color: #ff9800;
        `;
      case 'rejected':
        return `
          background-color: #feeeee;
          color: #dc3545;
        `;
      default:
        return '';
    }
  }}
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  color: var(--text-light);
  
  &:hover {
    background-color: var(--background-light);
  }
`;

const FormContent = styled.div`
  padding: 1.5rem;
  
  form {
    max-width: 800px;
    margin: 0 auto;
  }
  
  @media (max-width: 768px) {
    padding: 1rem 0.5rem;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
  
  label {
    display: block;
    margin-bottom: 0.5rem;
    font-weight: 500;
    color: var(--text-dark);
  }
  
  input, select, textarea {
    width: 100%;
    padding: 0.75rem;
    border: 1px solid var(--border);
    border-radius: 8px;
    font-family: 'Poppins', sans-serif;
    font-size: 1rem;
    color: var(--text-dark);
    background-color: ${props => props.theme === 'dark' ? '#383838' : '#f5f5f5'};
    
    &:focus {
      border-color: var(--primary);
      box-shadow: 0 0 0 2px rgba(43, 93, 161, 0.2);
    }
  }
  
  textarea {
    resize: vertical;
  }
`;

const FormRow = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;
  
  ${FormGroup} {
    flex: 1;
    margin-bottom: 0;
  }
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1.5rem;
  }
`;

const NoteBox = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 1rem;
  background-color: rgba(43, 93, 161, 0.1);
  border-radius: 8px;
  margin-bottom: 1.5rem;
`;

const NoteIcon = styled.div`
  font-size: 1.25rem;
`;

const NoteText = styled.div`
  font-size: 0.9rem;
  font-weight: 500;
  color: var(--text-dark);
`;

const FormActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 2rem;
`;

const SubmitButton = styled.button`
  background-color: var(--primary);
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0.75rem 1.5rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: var(--primary-light);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    background-color: #6c757d;
    
    &:hover {
      background-color: #6c757d;
    }
  }
`;

const CancelButton = styled.button`
  background-color: transparent;
  color: var(--text-dark);
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 0.75rem 1.5rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background-color: var(--background-light);
  }
`;

const AlertWarning = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  background-color: rgba(255, 193, 7, 0.1);
  border: 1px solid rgba(255, 193, 7, 0.3);
  border-radius: 8px;
  margin-bottom: 1.5rem;
`;

const AlertIcon = styled.div`
  font-size: 1.25rem;
  flex-shrink: 0;
`;

const AlertText = styled.div`
  font-size: 0.9rem;
  font-weight: 500;
  color: var(--text-dark);
`;

const TypeToggleContainer = styled.div`
  margin-bottom: 1.5rem;
`;

const TypeToggleLabel = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: var(--text-dark);
`;

const TypeToggleButtonGroup = styled.div`
  display: flex;
  border: 1px solid var(--border);
  border-radius: 8px;
  overflow: hidden;
  gap: 16px;
  padding: 8px;
  background-color: var(--background-light);
`;

const TypeToggleButton = styled.button`
  flex: 1;
  padding: 0.75rem 1.5rem;
  text-align: center;
  background-color: ${props => props.active === 'true' ? 'var(--primary)' : 'var(--background)'};
  color: ${props => props.active === 'true' ? 'white' : 'var(--text-dark)'};
  border: none;
  border-radius: 6px;
  cursor: ${props => props.disabled ? 'default' : 'pointer'};
  transition: all 0.2s;
  font-weight: 500;
  
  &:hover {
    background-color: ${props => {
    if (props.disabled) return props.active === 'true' ? 'var(--primary)' : 'var(--background)';
    return props.active === 'true' ? 'var(--primary)' : 'var(--background-light)';
  }};
  }
`;

const ErrorMessage = styled.div`
  background-color: ${props => props.theme === 'dark' ? '#3a1c1c' : '#feeeee'};
  color: ${props => props.theme === 'dark' ? '#ff6b6b' : '#dc3545'};
  padding: 0.75rem 1rem;
  border-radius: 8px;
  margin-bottom: 1.5rem;
  font-size: 0.875rem;
  border-left: 3px solid ${props => props.theme === 'dark' ? '#ff6b6b' : '#dc3545'};
  display: flex;
  align-items: center;
  
  &:before {
    content: "⚠️";
    margin-right: 0.5rem;
  }
`;

const SuccessMessage = styled.div`
  background-color: ${props => props.theme === 'dark' ? '#1a3a2a' : '#e6f8f5'};
  color: ${props => props.theme === 'dark' ? '#4ade80' : '#00a389'};
  padding: 0.75rem 1rem;
  border-radius: 8px;
  margin-bottom: 1.5rem;
  font-size: 0.875rem;
  border-left: 3px solid ${props => props.theme === 'dark' ? '#4ade80' : '#00a389'};
  display: flex;
  align-items: center;
  
  &:before {
    content: "✅";
    margin-right: 0.5rem;
  }
`;

const StudentSuggestions = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: var(--background);
  border: 1px solid var(--border);
  border-top: none;
  border-radius: 0 0 8px 8px;
  max-height: 200px;
  overflow-y: auto;
  z-index: 1000;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

const StudentSuggestion = styled.div`
  padding: 0.75rem;
  cursor: pointer;
  border-bottom: 1px solid var(--border);
  color: var(--text-dark);
  transition: background-color 0.2s;

  &:hover {
    background-color: var(--background-light);
  }

  &:last-child {
    border-bottom: none;
  }
`;

export default InterupsiForm;